import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, of, catchError } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { MockTableData } from '../../../mocks/mock-table-data';

import {
  ApiResponse,
  NotificationInfo,
  TableData,
  TableModel
} from '../../../models/table.model';

@Component({
  selector: 'app-table-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './tableDetailPage.component.html',
  styleUrls: ['./tableDetailPage.component.less']
})
export class TableDetailPageComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _http = inject(HttpClient);
  private _environment = inject(EnvironmentService);
  private _notifService = inject(NotificationService);
  tableData: TableData = { title: '', rows: [], totalCount: 0, columns: [] };
  notificationInfo: NotificationInfo = { title: '', body: '', type: '', created: '' };
  dynamicColumns: string[] = [];
  loading = signal(true);
  error = '';

  public formatCellValue = TableModel.formatCellValue;

  ngOnInit(): void {
    this.loadTableData();
  }

  private async loadTableData(): Promise<void> {
    const notificationId = this._route.snapshot.paramMap.get('id');
    if (!notificationId) {
      this.error = 'No notification ID provided';
      this.loading.set(false);
      return;
    }

    console.log('[TableDetailComponent] Loading data for notification:', notificationId);
    await this._notifService.markNotificationAsRead(notificationId);

    if (this._environment.isDesignMode) {
      const mockResponse = MockTableData.getTableDetail(notificationId) as ApiResponse;

      if (!mockResponse.success) {
        this.error = mockResponse.message || 'Failed to load mock data';
        this.loading.set(false);
        return;
      }

      const { notification, table } = TableModel.fromApiResponse(mockResponse);
      this.notificationInfo = notification;
      this.tableData = table;
      this.dynamicColumns = table.columns.map(c => c.key);

      this.loading.set(false);
      return;
    }

    const raw = await firstValueFrom(
      this._http
        .get<string>(`/api/notifications/${notificationId}/data`, { responseType: 'text' as 'json' })
        .pipe(
          catchError(err => {
            console.error('[TableDetailComponent] Error:', err);
            this.error = 'Error loading table data. Please try again.';
            this.loading.set(false);
            return of('{"success":false}');
          })
        )
    );

    console.log('[TableDetailComponent] Raw response:', raw);

    const parsed: ApiResponse = typeof raw === 'string' ? JSON.parse(raw) : (raw as unknown as ApiResponse);
    console.log('[TableDetailComponent] Parsed response:', parsed);

    if (!parsed.success) {
      this.error = parsed.message || 'Failed to load data';
      this.loading.set(false);
      return;
    }

    const { notification, table } = TableModel.fromApiResponse(parsed);
    this.notificationInfo = notification;
    this.tableData = table;
    this.dynamicColumns = table.columns.map(c => c.key);

    this.loading.set(false);
  }

  trackByIndex(index: number): number {
    return index;
  }
}
